import MouseTrap from "mousetrap";
import Aria from "../util/Aria";
import { CHAT_STATE, Constants, EMAIL_STATE } from "../model/Constants";
import { decodeEntities, generateBackgroundImgUrl, getChimeImage, getChimeText, addChimeToggleClass, shouldBeVisibleByStateCheck, returnTextOrDefaultTextByType } from "../util/Misc";
import PrintMgr from "../util/PrintMgr";
import ModalMgr from "../util/ModalMgr";
import {topbarelementRender} from "./templates/renders/topbarelement";
import ChatPosition from "../util/ChatPosition";
import { debounce } from "../chatframe/Utils";

declare const appInstance: any;

/**
 * Creates the top bar custom element which will be then registered to the ShoutUI Framework
 * @type {{template, created(): void, onIconClick(Event): void, tagName: string, updateTitle(string): void, udpateBackground(object=): void}}
 */
// eslint-disable-next-line object-shorthand
const TopBarElement: any = {
    tagName: "topbar-element",
    initialize(options) {
        initComponent(options);
    },
    renderer: topbarelementRender,
    /**
     * We're using a debounced function because we're listening to both click
     * and touchEnd events, and iPad will sometimes send both events for a single tap,
     * causing an unwanted double click.
     */
    onIconClick: debounce(function onIconClick(event: PointerEvent | MouseEvent | TouchEvent) { // eslint-disable-line no-unused-vars
        if (this.data.disabled) {
            return;
        }

        const type = this.prop.type;
        const modalManager = new ModalMgr(this.$app);

        switch (type) {
            case "Close":
                event.preventDefault();
                this.$app.closeChat();
                break;

            case "Minimize":
                event.preventDefault();
                this.$app.minimize();
                break;

            case "Email":
                modalManager.requestEmail();
                break;

            case "Chime":
                this.toggleChime();
                break;

            case "Print":
                PrintMgr.displayPrintWindow(this.$app);
                break;

            case "Info":
                modalManager.displayInfoModalWindow(this.prop.content);
                break;

            case "Move":
                ChatPosition.togglePosition();
                break;

            // no default
        }
    }, 50, true),
    onItemClick(event) {
        if (event.target.tagName.toLowerCase() === "a") {
            // Agent pushed links are send to Bootstrap for top window navigation
            this.$app.bridge.pushToFrameSet(event.target.href, event.target.target);
            event.preventDefault();
        }
    },

    created() {
        this.ariaUtil = new Aria(this.$app);

        this.iconEl = this.el.querySelector(".icon")
            || this.el.querySelector(".logo");
        this.visible = true;

        if (this.prop.extended) {
            this.extendedButton = this.el.querySelector(".extended-topbar-button");
        }

        this.processProperties(this.prop);
        this.toggleFullScreenMode(this.$app.getBridge().isFullScreen());

        /**
         * Keybinding library for action on button focus
         * @type {Mousetrap}
         * @private
         */
        if (this.iconEl) {
            this.iconElMouseTrap = new MouseTrap(this.extendedButton || this.iconEl);
            this.setKeyboardBindings();
        }

        if (this.prop.type == "chime" && this.visible) {
            addChimeToggleClass(this.$app.isSoundEnabled(), this.el);
        }
    },

    destroy() {
        this.unsetKeyboardBindings();
    },

    /**
     * Bind keyboard keys to different actions
     * @private
     */
    setKeyboardBindings() {
        /**
         * Bind keys to trigger click action on iconEl
         */
        this.iconElMouseTrap.bind(["enter", "space"], keyboardEvent => {
            this.onIconClick(keyboardEvent);

            // return false to call preventDefault and stop propagation
            return false;
        });
    },

    /**
     * Remove keyboard bindings
     * @private
     */
    unsetKeyboardBindings() {
        this.iconElMouseTrap.unbind(["enter", "space"]);
    },

    /**
     * Process current element properties (dom attributes) and setup proper
     * states and handlers based on the values assigned.
     *
     * @param {Object} propObj - properties object
     */
    processProperties(propObj) {
	    this.processHiddenDevice(propObj);

        this.processHiddenStatesProperty(propObj);

        this.data.text = returnTextOrDefaultTextByType(propObj);

        this.processTypeProperty(propObj);

        // init background based on props
        this.udpateBackground(propObj);
    },

    /**
     * Handle any hiddenstates property set to this control
     * @param {Object} propObj - properties object
     */
    processHiddenStatesProperty(propObj) {
        if (propObj.hiddenstates && this.visible) {
            this.handleComponentVisibility(this.$app.getStateMgr().state.state);
            this.subscribeToStateMgr();
        }
    },
    processHiddenDevice(propObj) {
        if (propObj.hiddendevices) {
            if (propObj.hiddendevices
                .indexOf(this.$app.getSDKInstance().getDeviceType()) !== -1) {
                this.visible = false;
                this.el.style.display = "none";
            }
        }
    },

    /**
     * Handle any "type" property set to this control
     * @param {Object} propObj - properties object
     */
    processTypeProperty(propObj) {
        switch (propObj.type) {
            case "Description":

                // falls through
            case "Title":
                this.processTypePropertyTitle(propObj);
                break;

            case "Chime":
                this.updateChimeButtonStateForAria();
                break;

            case "Email":
                this.processTypePropertyEmail(propObj);
                break;

			// no default
        }
    },

    /**
     * Things to set if title "type" is set.
     * @param {Object} propObj - properties object
     */
    processTypePropertyTitle(propObj) {
	    if (propObj.subtext) {
            this.data.subText = decodeEntities(
                propObj.subtext.replace("undefined", "")
            );
        }

        this.data.mainTitle = decodeEntities(propObj.text);

        this.data.subText = this.ariaUtil.injectAttributes(
            this.data.subText
        );
        this.data.mainTitle = this.ariaUtil.injectAttributes(
            this.data.mainTitle
        );
    },

    /**
     * Things to set if email "type" is set.
     * @param {Object} propObj - properties object
     */
    processTypePropertyEmail(propObj) {
        if (!propObj.hiddenstates) {
            this.subscribeToStateMgr();
        }

        const stateMgr = this.$app.getStateMgr();
        this.emailStateListenerIndex = stateMgr.subscribe(
            emailState => {
                this.disableData(emailState);
            }, "emailState"
        );
    },

    subscribeToStateMgr() {
        if (typeof this.stateListenerIndex === "undefined") {
            const stateMgr = this.$app.getStateMgr();
            this.stateListenerIndex = stateMgr.subscribe(
                (state) => this.chatStateListener(state),
                "state"
            );
        }
    },

    /**
     * Update the Chime icon's state for aria announcement
     */
    updateChimeButtonStateForAria() {
	    // If sound enabled we show sound off text in button, but the title does not reflect according to the text
        const text = getChimeText(this.data.text, this.$app);
        (this.extendedButton ? this.extendedButton : this.iconEl).setAttribute("title", text);
    },

    /**
     * Update the top bar element background image
     * @param {ImageO} propObj - property object
     */
    udpateBackground(propObj) {
        let imgObj = propObj;

        if (propObj.type === "Chime") {
            imgObj = getChimeImage(propObj, this.$app);

            if ((propObj.text || (appInstance.audioTextOn && appInstance.audioTextOff)) && propObj.extended) {
                const text = getChimeText(propObj.text, this.$app);
                this.el.querySelector(".extended-text").innerText = text;
            }
        }

        const imgUrl = generateBackgroundImgUrl(imgObj, this.$app);

        if (imgUrl) {
            this.iconEl.style.backgroundImage = imgUrl;
            this.iconEl.style.display = "";
        } else if (this.iconEl) {
            this.iconEl.style.display = "none";
        }

        if (propObj.extended && this.data.disabled) {
            this.el.classList.add("disabled");
        }
    },

    /**
     * Update the top bar element inner text
     * @param {String} title - title to update to
     */
    updateTitle(title) {
        this.iconEl.querySelector(".main-title span").innerHTML = title;
    },
    updateStatus(status, subStatus) {
        this.data.status = status || "";
        this.data.subStatus = subStatus || "";
    },
    enable() {
        // this.iconEl.classList.remove("disabled")
        this.data.disabled = false;

        if (this.prop.extended) {
            this.el.classList.remove("disabled");
        }

        this.udpateBackground(this.prop);
    },
    setDisable() {
        // this.iconEl.classList.add("disabled")
        this.data.disabled = true;
        this.udpateBackground(this.prop);
    },
    toggleChime() {
        this.$app.setSoundState(!this.$app.isSoundEnabled());
        this.updateChimeButtonStateForAria();
        this.udpateBackground(this.prop);
        addChimeToggleClass(this.$app.isSoundEnabled(), this.el);
    },
    toggleFullScreenMode(state) {
        if (this.data.isFullScreenOnly) {
            this.iconEl.style.display = state ? "" : "none";
        }
    },

    /**
     * passing engaged state so that if any of the menu buttons
     * need to be enabled
     * @param {String} state - State string
     */
    chatStateListener(state) {
        if (this.prop.type === "Email") {
            this.enableData(state);
        }

        this.handleComponentVisibility(state);
    },
    enableData(state) {
        const isEngaged = (state === CHAT_STATE.ENGAGED);
        const isEmailNotRequested = !this.$app.isEmailRequested();
        const isDisabled = this.data.disabled;

        if (isEngaged && isEmailNotRequested && isDisabled) {
            this.enable();
        }
    },
    disableData(state) {
        const emailStateSetToRequested = (state === EMAIL_STATE.REQUESTED);
        const isDisabled = this.data.disabled;

        if (emailStateSetToRequested && !isDisabled) {
            this.setDisable();
        }
    },
    handleComponentVisibility(state){
        if (!this.prop.hiddenstates) {
            return;
        }

        let visibility = shouldBeVisibleByStateCheck(state, this.prop.hiddenstates);

        if (visibility && !this.visible) {
            this.el.style.display = "";
        } else if (!visibility && this.visible) {
            this.el.style.display = "none";
        }

        this.visible = visibility;
    },
    removed() {
        const stateMgr = this.$app.getStateMgr();

        if (typeof this.emailStateListenerIndex !== "undefined") {
            stateMgr.unsubscribe(this.emailStateListenerIndex);
        }

        if (typeof this.stateListenerIndex !== "undefined") {
            stateMgr.unsubscribe(this.stateListenerIndex);
        }
    },
};

/**
 * @export TopBarElement
 * @param {CIApp} appInstance
 * @return {{template, created, (): void, onIconClick, (Event): void, tagName: string, updateTitle, (string): void, udpateBackground, (Object=): void}}
 */
export default appInstance => {
    TopBarElement.$app = appInstance;

    return TopBarElement;
};

const initComponent = (options: any) => {
    const appInstance = options.$app;
    const appConfig = appInstance.getUIConfig();

    if (appConfig.topBarConfig) {
        const tpConfig = appConfig.topBarConfig;
        options.constants = {
            topBarIconStyle: tpConfig.iconStyles,
            topBarTitleStyle: tpConfig.titleStyles,
            topBarLogoStyle: tpConfig.logoStyles,
            topBarDescriptionStyle: tpConfig.descriptionStyles,
            topBarStatusStyle: tpConfig.statusStyles,
            extendedIconStyle: tpConfig.extendedTopBarItemIconStyles,
        };
    }

    options.data = {
        mainTitle: "",
        status: "",
        subStatus: "",
        subText: "",
        text: "",

        disabled: state => {
            if (state.prop.type === "Email") {
                if (!appInstance.getSDKInstance().isChatInProgress()) {
                    return true;
                }

                if (appInstance.isEmailRequested()) {
                    return true;
                }
            }

            return false;
        },

        isFullScreenOnly: state => {
            if (state.prop.mode === Constants.IS_FULL_SCREEN) {
                return true;
            }

            return false;
        },
    };
};
